import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import apiService from '../services/api'
import { User, ShoppingBag, ClipboardList } from 'lucide-react'
import toast from 'react-hot-toast'

const Account = () => {
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const navigate = useNavigate()
  const [params] = useSearchParams()

  useEffect(() => {
    const token = localStorage.getItem('user_token')
    if (!token) {
      navigate(`/login?redirect=${encodeURIComponent('/account')}`)
      return
    }
    apiService.get('/auth/me', { params: { t: Date.now() } })
      .then(res => setCustomer(res.data?.user || null))
      .catch(()=>setError('Erreur chargement'))
      .finally(()=>setLoading(false))
  }, [])

  const handleChange = (field, value) => {
    setCustomer(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const payload = {
        first_name: customer.first_name,
        last_name: customer.last_name,
        phone: customer.phone,
        company_name: customer.company_name,
        street: customer.street,
        street_number: customer.street_number,
        city: customer.city,
        postal_code: customer.postal_code,
      }
      const res = await apiService.put('/users/me', payload)
      setCustomer(res.data.user)
      try {
        const me = await apiService.get('/auth/me', { params: { t: Date.now() } })
        setCustomer(me.data?.user || res.data.user)
      } catch {}
      toast.success('Profil mis à jour')
      const redirect = params.get('redirect')
      if (redirect) navigate(redirect)
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="container-custom py-8">Chargement…</div>
  if (error) return <div className="container-custom py-8 text-red-600">{error}</div>

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <User className="h-8 w-8 text-orange-500 mr-3" />
            Mon Compte
          </h1>
          <p className="mt-2 text-gray-600">
            Gérez vos informations personnelles et vos préférences
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation gauche */}
          <div className="lg:col-span-1">
            <div className="card">
              <nav className="space-y-2">
                <button className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm bg-orange-50 text-orange-600 border border-orange-100 shadow-sm`}>
                  <User className="h-4 w-4 mr-2" />
                  Coordonnées
                </button>
                <Link 
                  to="/account/reservations"
                  className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm hover:bg-gray-50 text-gray-700 hover:text-gray-900"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Mes réservations
                </Link>
                <Link 
                  to="/account/favorites"
                  className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm hover:bg-gray-50 text-gray-700 hover:text-gray-900"
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Mes listes
                </Link>
              </nav>
            </div>
          </div>

          {/* Contenu droite */}
          <div className="lg:col-span-3">
            <div className="card space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                  <input className="input-field" value={customer?.first_name || ''} onChange={e=>handleChange('first_name', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                  <input className="input-field" value={customer?.last_name || ''} onChange={e=>handleChange('last_name', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input className="input-field" value={customer?.email || ''} disabled />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <input className="input-field" value={customer?.phone || ''} onChange={e=>handleChange('phone', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise</label>
                  <input className="input-field" value={customer?.company_name || ''} onChange={e=>handleChange('company_name', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <input className="input-field" placeholder="N°" value={customer?.street_number || ''} onChange={e=>handleChange('street_number', e.target.value)} />
                    <input className="input-field col-span-2" placeholder="Rue" value={customer?.street || ''} onChange={e=>handleChange('street', e.target.value)} />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input className="input-field" placeholder="Code postal" value={customer?.postal_code || ''} onChange={e=>handleChange('postal_code', e.target.value)} />
                    <input className="input-field col-span-2" placeholder="Ville" value={customer?.city || ''} onChange={e=>handleChange('city', e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="text-right">
                <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Account


